/**
 * Rheina marketing signup — Shopify customer newsletter with modal feedback.
 * Used in the homepage newsletter section and site footer.
 */
import { Component } from '@theme/component';

class RheinaMarketingSignup extends Component {
  /** @type {number | null} */
  #autoCloseTimer = null;

  connectedCallback() {
    super.connectedCallback();
    const form = this.querySelector('[data-newsletter-form]');
    form?.addEventListener('submit', this.#handleSubmit);

    const closeBtn = this.querySelector('[data-modal-close]');
    const backdrop = this.querySelector('[data-modal-backdrop]');
    closeBtn?.addEventListener('click', this.#closeModal);
    backdrop?.addEventListener('click', this.#closeModal);

    document.addEventListener('keydown', this.#onKeydown);

    if (this.dataset.showSuccess === 'true') {
      this.#openModal();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const form = this.querySelector('[data-newsletter-form]');
    form?.removeEventListener('submit', this.#handleSubmit);
    document.removeEventListener('keydown', this.#onKeydown);
    if (this.#autoCloseTimer !== null) clearTimeout(this.#autoCloseTimer);
    document.documentElement.classList.remove('rheina-newsletter--modal-open');
  }

  #onKeydown = (event) => {
    const modal = this.querySelector('[data-success-modal]');
    if (event.key === 'Escape' && modal?.dataset.open === 'true') this.#closeModal();
  };

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    event.preventDefault();

    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;

    const emailInput = form.querySelector('input[name="contact[email]"]');
    if (!(emailInput instanceof HTMLInputElement)) return;

    const email = emailInput.value.trim();
    if (!email) return;

    const submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn instanceof HTMLButtonElement) {
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');
    }

    this.#clearInlineError();

    try {
      const response = await fetch('/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
        },
        body: new URLSearchParams({
          form_type: 'customer',
          utf8: '✓',
          'contact[email]': email,
          'contact[tags]': 'newsletter',
          'customer[accepts_marketing]': 'true',
        }).toString(),
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        if (response.ok) {
          emailInput.value = '';
          this.#openModal();
          return;
        }
        throw new Error('Invalid response');
      }

      if (data?.errors) {
        const emailErrors = data.errors.email || data.errors['contact[email]'];
        const message = Array.isArray(emailErrors) ? emailErrors[0] : emailErrors;
        this.#showInlineError(message || this.dataset.errorMessage || 'Please enter a valid email address.');
        return;
      }

      if (!response.ok && !data?.posted_successfully) {
        this.#showInlineError(this.dataset.errorMessage || 'Something went wrong. Please try again.');
        return;
      }

      emailInput.value = '';
      this.#openModal();
    } catch {
      this.#showInlineError(this.dataset.errorMessage || 'Something went wrong. Please try again.');
    } finally {
      if (submitBtn instanceof HTMLButtonElement) {
        submitBtn.disabled = false;
        submitBtn.removeAttribute('aria-busy');
      }
    }
  };

  #showInlineError = (message) => {
    const errorEl = this.querySelector('[data-inline-error]');
    const inputWrap = this.querySelector('[data-input-wrap]');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
    inputWrap?.classList.add('rheina-marketing-signup__input-wrap--error');
  };

  #clearInlineError = () => {
    const errorEl = this.querySelector('[data-inline-error]');
    const inputWrap = this.querySelector('[data-input-wrap]');
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
    inputWrap?.classList.remove('rheina-marketing-signup__input-wrap--error');
  };

  #openModal = () => {
    const modal = this.querySelector('[data-success-modal]');
    if (!modal) return;

    modal.dataset.open = 'true';
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('rheina-newsletter--modal-open');

    const autoClose = Number.parseInt(this.dataset.autoCloseMs || '0', 10);
    if (autoClose > 0) {
      if (this.#autoCloseTimer !== null) clearTimeout(this.#autoCloseTimer);
      this.#autoCloseTimer = window.setTimeout(this.#closeModal, autoClose);
    }

    const closeBtn = modal.querySelector('[data-modal-close]');
    if (closeBtn instanceof HTMLElement) closeBtn.focus();
  };

  #closeModal = () => {
    const modal = this.querySelector('[data-success-modal]');
    if (!modal) return;

    modal.dataset.open = 'false';
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('rheina-newsletter--modal-open');

    if (this.#autoCloseTimer !== null) {
      clearTimeout(this.#autoCloseTimer);
      this.#autoCloseTimer = null;
    }
  };
}

if (!customElements.get('rheina-marketing-signup')) {
  customElements.define('rheina-marketing-signup', RheinaMarketingSignup);
}

if (!customElements.get('rheina-newsletter')) {
  customElements.define('rheina-newsletter', RheinaMarketingSignup);
}
